import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { Loader2, Check, X as XIcon, AtSign } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { AuthService } from '../services/api';
import { UsernameService } from '../services/usernameService';
import { useTheme } from './ThemeProvider';
import { toast } from 'sonner';

interface GoogleUsernameModalProps {
  open: boolean;
  /** Google profile from the sign-in step, used to seed a suggestion. */
  profile: { uid: string; email: string; name: string; photoURL: string } | null;
  onComplete: () => void;
  onCancel: () => void;
}

/**
 * The one thing Google can't give us: a username. Shown between Google auth and
 * account creation for brand-new accounts, because AfroYap usernames must be
 * unique and the old code derived one from the email local-part with no check
 * (which collides and throws USERNAME_TAKEN_IN_REGISTRY at doc-creation time).
 *
 * Availability is checked live via the same UsernameService the profile setup
 * screen uses; the authoritative guarantee is still the transactional registry
 * claim inside createUserDocument, which this surfaces as a friendly error.
 */
export function GoogleUsernameModal({ open, profile, onComplete, onCancel }: GoogleUsernameModalProps) {
  const { theme } = useTheme();
  const [username, setUsername] = useState('');
  const [status, setStatus] = useState<'idle' | 'checking' | 'available' | 'taken' | 'invalid'>('idle');
  const [submitting, setSubmitting] = useState(false);

  // Seed from the Google email local-part as a *suggestion* only — the user can
  // change it, and it's still validated like any other choice.
  useEffect(() => {
    if (!open || !profile) return;
    const seed = (profile.email || '').split('@')[0].replace(/[^a-zA-Z0-9_]/g, '').toLowerCase();
    setUsername(seed.slice(0, 20));
    setStatus('idle');
  }, [open, profile]);

  useEffect(() => {
    const clean = username.trim().replace('@', '').toLowerCase();
    if (!clean) { setStatus('idle'); return; }
    if (clean.length < 3 || !/^[a-z0-9_]+$/.test(clean)) { setStatus('invalid'); return; }

    let cancelled = false;
    setStatus('checking');
    const t = setTimeout(async () => {
      try {
        const available = await UsernameService.checkUsernameAvailability(clean);
        if (!cancelled) setStatus(available ? 'available' : 'taken');
      } catch {
        if (!cancelled) setStatus('idle');
      }
    }, 450);
    return () => { cancelled = true; clearTimeout(t); };
  }, [username]);

  if (!open || typeof document === 'undefined') return null;

  const clean = username.trim().replace('@', '').toLowerCase();
  const canSubmit = status === 'available' && !submitting;

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      await AuthService.completeGoogleSignup(clean);
      onComplete();
    } catch (e: any) {
      const msg = String(e?.message || e);
      if (/USERNAME_TAKEN/i.test(msg)) {
        // Someone claimed it between the check and the write.
        setStatus('taken');
        toast.error('That username was just taken — try another.');
      } else {
        console.error('[GoogleUsernameModal] signup failed:', e);
        toast.error('Could not finish creating your account.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const hint = {
    idle:      '3+ characters, letters, numbers and underscores.',
    checking:  'Checking availability…',
    available: `@${clean} is available.`,
    taken:     `@${clean} is already taken.`,
    invalid:   'Use at least 3 characters — letters, numbers or underscores only.',
  }[status];

  return createPortal(
    <div className="fixed inset-0 z-[10000005] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" />
      <div
        className="relative w-full max-w-md rounded-3xl border border-black/10 dark:border-white/10 shadow-2xl p-6"
        style={{ backgroundColor: theme === 'dark' ? '#1a1a1a' : '#FAF8F5', color: theme === 'dark' ? '#FFFFFF' : '#000000' }}
      >
        <h2 className="text-xl mb-1" style={{ fontWeight: 700 }}>Choose your username</h2>
        <p className="text-sm mb-5" style={{ color: theme === 'dark' ? '#999999' : '#666666' }}>
          {profile?.name ? `Welcome, ${profile.name.split(' ')[0]}. ` : ''}
          This is how people will find you on AfroYap.
        </p>

        <div className="relative mb-2">
          <AtSign className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
          <Input
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            placeholder="username"
            autoCapitalize="none"
            autoCorrect="off"
            maxLength={20}
            className="w-full h-12 pl-9 pr-10 rounded-xl"
          />
          <div className="absolute right-3 top-1/2 -translate-y-1/2">
            {status === 'checking' && <Loader2 className="w-4 h-4 animate-spin text-zinc-400" />}
            {status === 'available' && <Check className="w-4 h-4 text-green-500" />}
            {(status === 'taken' || status === 'invalid') && <XIcon className="w-4 h-4 text-red-500" />}
          </div>
        </div>

        <p
          className="text-xs mb-5"
          style={{
            color: status === 'available' ? '#22c55e'
              : (status === 'taken' || status === 'invalid') ? '#ef4444'
              : (theme === 'dark' ? '#999999' : '#666666'),
          }}
        >
          {hint}
        </p>

        <Button
          onClick={handleSubmit}
          disabled={!canSubmit}
          className="w-full h-12 rounded-xl bg-brand-gradient text-white disabled:opacity-50"
          style={{ fontWeight: 700 }}
        >
          {submitting ? <Loader2 className="w-5 h-5 animate-spin" /> : 'Create my account'}
        </Button>
        <button
          onClick={onCancel}
          disabled={submitting}
          className="w-full mt-3 text-sm"
          style={{ color: theme === 'dark' ? '#999999' : '#666666' }}
        >
          Cancel
        </button>
      </div>
    </div>,
    document.body,
  );
}
